import { isEmpty, keyBy, reverse } from 'lodash';
import moment from 'moment';
import naturalCompare from 'natural-compare-lite';
import { User } from 'api/models/core-types';
import { API_DATE_FORMAT } from 'components/atoms/due-date-picker';
import { humanise } from 'components/atoms/humanised-date';
import {
  AssigneeValue,
  FieldType,
  SingleSelectField,
  UserId as AssgineeUserId,
} from 'components/organisms/tasks/fields';
import { PRIORITY_FIELD_NAME } from 'data/fields/hooks';
import { Translate } from 'hooks/use-translation';
import { FIELD_ID_PROJECT_PHASE_FIELD, TaskField } from 'stores/fields';
import { Project } from 'stores/projects';
import { FieldHelper } from 'utils/field';
import { fullName } from 'utils/full-name';
import { getRandomColor } from 'utils/get-random-color';
import { unreachable } from 'utils/unreachable';
import { Task } from '../../../stores/tasks';
import type { Column } from './types';

export const generateDroppableId = (swimlaneId: string, columnId: string) => {
  return [swimlaneId, columnId].join('.');
};

export const parseDroppableId = (droppableId: string): string[] => {
  return droppableId.split('.');
};

export const generateDraggableId = (
  swimlaneId: string,
  columnId: string,
  itemId: string,
) => {
  return [swimlaneId, columnId, itemId].join('.');
};

export const parseDraggableId = (draggableId: string): string[] => {
  const parsed = draggableId.split('.');

  return parsed;
  /* switch (parsed.length) {
    case 1:
      return [undefined, undefined, parsed[0]];
    case 2:
      return [undefined, parsed[0], parsed[1]];
    case 3:
      return parsed;
    default:
      return unreachable(parsed as never);
  } */
};

const emptyFilterer = (value: unknown): boolean =>
  value === null || value === undefined;

const toColumns = (field: SingleSelectField): Column[] => {
  const columns = FieldHelper(field).options.activeOptions.map(
    ({ value, color, label, position }, index): Column => {
      return {
        colId: String(value),
        value,
        dataKey: field.fieldName,
        label,
        color,
        position: position ?? index,
      };
    },
  );

  const emptyField: Column = {
    colId: 'null',
    value: null,
    dataKey: field.fieldName,
    label: 'Empty',
    color: 'gray',
    position: columns.length,
    valueFilterer: emptyFilterer,
  };

  switch (field.fieldName) {
    case 'project':
    case 'status':
      return columns;
    case PRIORITY_FIELD_NAME:
      return reverse([...columns, emptyField]).map((col, index) => ({
        ...col,
        position: index,
      }));
    default:
      return [...columns, emptyField];
  }
};

const getColumns = (field: TaskField): Column[] => {
  switch (field.type) {
    case FieldType.SingleSelect:
      return toColumns(field);

    default:
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      return unreachable(field);
  }
};

const dueDateColumns = (translate: Translate): Column[] => {
  const overdue: Column = {
    colId: 'overdue',
    value: moment().subtract(1, 'day').format(API_DATE_FORMAT),
    dataKey: 'dueDate',
    collapsed: false,
    label: translate('over_due'),
    color: 'red',
    position: 1,
    isDropDisabled: true,
    disableAdd: true,
    valueFilterer: (value, record: Task) => {
      return (
        moment(value).isBefore(moment().startOf('day')) && !record.completedAt
      );
    },
  };

  const today = moment().startOf('day');
  const tomorrow = today.clone().add(1, 'day');
  const opts = [today];

  Array.from(Array(6).keys()).map((value) => {
    return opts.push(today.add(value + 1, 'day'));
  });

  const result: Column[] = opts.map((date, index): Column => {
    const formattedDate = date.format(API_DATE_FORMAT);
    const humanisedDate = humanise(date);

    return {
      colId: formattedDate,
      value: formattedDate,
      dataKey: 'dueDate',
      collapsed: false,
      label: date.isSame(today, 'day')
        ? translate('today.with_date', { date: humanisedDate })
        : date.isSame(tomorrow, 'day')
        ? translate('tomorrow.with_date', { date: humanisedDate })
        : humanisedDate,
      position: index + 2,
      color: getRandomColor(date.date()),
      valueFilterer: (value /* , record */) =>
        value
          ? moment(value).startOf('day').isSame(date.startOf('day'))
          : false,
    };
  });
  const futureDate = moment().add(opts.length, 'days').startOf('day');
  const future: Column = {
    colId: futureDate.format(API_DATE_FORMAT),
    value: futureDate.format(API_DATE_FORMAT),
    dataKey: 'dueDate',
    collapsed: false,
    position: 10,
    label: translate('future'),
    color: 'gray',
    isDropDisabled: true,
    disableAdd: true,
    valueFilterer: (value) =>
      value ? moment(value).startOf('day').isSameOrAfter(futureDate) : false,
  };

  const empty: Column = {
    colId: 'null',
    value: null,
    dataKey: 'dueDate',
    collapsed: false,
    position: 11,
    label: translate('filter.due_date.no_due'),
    color: 'gray',
    valueFilterer: emptyFilterer,
  };

  return [overdue, ...result, future, empty];
};

type ColumnLookup = Record<string, Record<string, Column>>;

const assigneeColumns = (users: User[]): Column[] => {
  const transformed = users
    .map((user) => {
      return {
        value: [AssgineeUserId(user?.userId)],
        label: fullName(user),
        color: getRandomColor(user?.userId),
      };
    })
    .sort((itemA, itemB) => naturalCompare(itemA.label, itemB.label));

  const cols = transformed.map(({ value, label, color }, index): Column => {
    return {
      colId: AssigneeValue.toString(value[0]),
      value,
      color,
      label,
      dataKey: 'assignee',
      position: index,
      valueFilterer: (_value: AssigneeValue[] | undefined) => {
        /* NOTE:: please check this when we support multiselect field. */
        return (
          (_value ?? []).filter((v) => AssigneeValue.isSame(value[0], v))
            .length > 0
        );
      },
    };
  });

  cols.push({
    colId: 'null',
    value: [],
    color: 'gray',
    label: 'Empty',
    dataKey: 'assignee',
    position: cols.length,
    valueFilterer: (_value) => isEmpty(_value),
  });

  return cols;
};

const projectPhaseColumns = (field: TaskField, project?: Project): Column[] => {
  const columns =
    project?.phases?.map(
      ({ projectPhaseName: label, projectPhaseId: value }, index): Column => {
        return {
          colId: String(label),
          value: label,
          dataKey: field.fieldName,
          label,
          color: getRandomColor(value),
          position: index,
        };
      },
    ) ?? [];

  const emptyField: Column = {
    colId: 'null',
    value: null,
    dataKey: field.fieldName,
    label: 'Empty',
    color: 'gray',
    position: columns.length,
    valueFilterer: emptyFilterer,
  };

  return [...columns, emptyField];
};

export const ALLOWED_COLUMN_FIELD_NAMES = [
  'status',
  'assignee',
  'priority',
  'dueDate',
];

export const toLookup = (
  fields: TaskField[],
  translate: Translate,
  users: User[],
  project?: Project,
): ColumnLookup => {
  return fields.reduce((prev, field) => {
    let cols;

    if (field.type === FieldType.Assignee) {
      cols = assigneeColumns(users);
    } else if (field.type === FieldType.Date) {
      cols = dueDateColumns(translate);
    } else if (field.fieldId === FIELD_ID_PROJECT_PHASE_FIELD) {
      cols = projectPhaseColumns(field, project);
    } else {
      cols = getColumns(field);
    }

    return {
      ...prev,
      [field.fieldName]: keyBy(cols, 'colId'),
    };
  }, {});
};
